import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, effect, ElementRef, EventEmitter, HostBinding, HostListener, Input, NgZone, OnDestroy, OnInit, Output, signal, untracked, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { IEndpoint, StreamTypes } from "@auvious/rtc";
import { merge } from "rxjs";
import { filter, take } from "rxjs/operators";
import { PictureInPictureStore } from "../../services/store/picture-in-picture.store";


import { fadeInOut } from "../../core-ui.animations";
import {
  ConversationOriginEnum,
  ConversationTypeEnum,
  EndpointTypeEnum,
  LayoutEnum,
  ScreenShareSurfaceEnum,
  TileTypeEnum,
  UserRoleEnum,
  VideoFacingModeEnum,
} from "../../core-ui.enums";
import {
  CustomerParam,
  IArea,
  IEndpointMetadata,
  IStreamMetadataChangedEvent,
  IThemeOptions,
  ITile,
  PublicParam,
  ThemeParam,
} from "../../models";
import { IInteraction } from "../../models/IInteraction";
import {
  SketchService,
  AppConfigService,
  ArPointerActivatedEvent,
  ArPointerDeactivatedEvent,
  ConferenceService,
  ConferenceStore,
  debugError,
  DeviceService,
  EndpointState,
  LocalMediaService,
  MediaEffectsService,
  MediaRulesService,
  PointerService,
  StreamState,
  ThemeService,
  UserService,
} from "../../services";
import { NotificationService } from "../../services/notification.service";
import { BandwidthAdaptationService } from "../../services/bandwidth.adaptation.service";
import { TranslateService } from "@ngx-translate/core";

const SKETCH_ACTIONS_COLUMN_LAYOUT_BREAKPOINT = 540;
const TINY_VIEW_LAYOUT_BREAKPOINT = 180;

@Component({
  selector: "app-tile",
  templateUrl: "./tile.component.html",
  styleUrls: ["./tile.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInOut],
  standalone: false,
})
export class TileComponent implements OnInit, AfterViewInit, OnDestroy, ITile {
  private host = inject<ElementRef<HTMLDivElement>>(ElementRef);
  private conferenceService = inject(ConferenceService);
  private deviceService = inject(DeviceService);
  private cd = inject(ChangeDetectorRef);
  private mediaFilterService = inject(MediaEffectsService);
  private notification = inject(NotificationService);
  private zone = inject(NgZone);
  private userService = inject(UserService);
  private pointerService = inject(PointerService);
  private mediaRules = inject(MediaRulesService);
  private mediaService = inject(LocalMediaService);
  private config = inject(AppConfigService);
  private themeService = inject(ThemeService);
  store = inject(ConferenceStore);
  bandwidthAdaptation = inject(BandwidthAdaptationService);
  translation = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private pipStore = inject(PictureInPictureStore, { optional: true });

  private _stream: StreamState;

  @Input() set stream(s: StreamState) {
    this._stream = s;

    if (this.userState?.id !== s.originator.endpoint) {
      this.userState = this.store.getEndpoint(s.originator.endpoint);
    }
  }

  public get stream(): StreamState {
    return this._stream;
  }

  @Input() interaction: IInteraction;

  @Input() isSpotlight: boolean;

  @Output() ready = new EventEmitter<TileComponent>();
  @Output() destroyed = new EventEmitter<TileComponent>();
  @Output() viewSizeChanged = new EventEmitter<IArea>();

  private userState: EndpointState;
  streamVolume: number;
  ArPointerOn = false;

  isFreezeFrameNotFromMute = false;
  isZoomOn = false;
  isStreamElementReady = false;
  isFilterLoading = false;
  isTinyView = signal(false);
  isAvatarError = signal(false);
  isSketchControlActive = false;
  isPointerActive = signal(false);
  isFullscreenActive = signal(false);
  isPopedOut = false;
  isSketchActionsCondensed = false;
  isWhisper = false;

  hostSize = signal<IArea>({ width: 0, height: 0 });
  sketchId = signal<string>(undefined);
  tileLayoutType = signal<LayoutEnum>(undefined);
  roomLayoutType = signal<LayoutEnum>(undefined);
  participant = signal<IEndpoint<IEndpointMetadata>>(undefined);
  shortName = signal<string>(undefined);
  theme = signal<IThemeOptions>(undefined);

  isAIAgent = computed(
    () => this.participant()?.metadata?.type === EndpointTypeEnum.aiAssistant
  );

  /**
   * While annotating on a fullscreen tile, the video renders `contain` so that the
   * sketch canvas and the laser point area can match the rendered video rect.
   */
  isVideoContained = computed(
    () => this.isFullscreenActive() && this.isPointerActive()
  );

  protected element: HTMLVideoElement;
  private _ratio = 1;

  // an AR session can start or stop while the tile is already fullscreen,
  // which flips the video between cover and contain
  private viewSizeEffect = effect(() => {
    this.isVideoContained();
    // the resize observer already covers host size changes
    untracked(() => this.updateViewSize());
  });

  ngOnInit(): void {
    this.themeService.themeChanged$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((t) => {
        this.theme.set(t);
      });

    this.isWhisper = !!this.interaction?.getWhisperMode();

    this.host.nativeElement.setAttribute("role", "group");
    this.setAriaLabel();

    merge(
      this.conferenceService.streamState$,
      this.conferenceService.endpointState$
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => {
        this.setAriaLabel();
        this.cd.detectChanges();
      });

    this.store.updated$
      .pipe(
        filter(
          () =>
            !!this.store.layout &&
            this.roomLayoutType() !== this.store.layout?.type
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.roomLayoutType.set(this.store.layout.type);
      });
    this.conferenceService.participantsChanged$
      .pipe(
        filter(
          (s) => !!this.conferenceService.getParticipants()[this.endpoint]
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((participants) => {
        const p = participants[this.endpoint];
        this.participant.set(p);
        try {
          const name = this.participant()?.metadata?.name?.trim();
          if (!name) return;

          this.shortName.set(
            name
              .split(" ")
              .reduce(
                (short, fragment) =>
                  (short +=
                    fragment?.length > 0 ? fragment[0].toUpperCase() : ""),
                ""
              )
          );
        } catch (ex) {
          debugError(ex);
        }
      });

    this.mediaFilterService.filterLoading$
      .pipe(filter((_) => this.isLocal), takeUntilDestroyed(this.destroyRef))
      .subscribe((_) => {
        this.isFilterLoading = true;
        this.cd.detectChanges();
      });

    merge(
      this.mediaFilterService.filterLoaded$,
      this.mediaFilterService.filterDisabled$
    )
      .pipe(filter(() => this.isLocal), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isFilterLoading = false;
        this.cd.detectChanges();
      });
    // take(1), not to spam ui with errors
    this.mediaFilterService.filterError$
      .pipe(
        filter(() => this.isLocal),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((error) => {
        this.isFilterLoading = false;
        let body = "Could not apply effect to video.";

        switch (error) {
          case "ImageUnusable":
            body = "Could not load image";
            break;
          case "PublishFailed":
          case "UnpublishFailed":
            body = "Could not notify participants of the effect change";
            break;
          case "VideoElementNotFound":
          case "VideoTrackNotFound":
            body = "Could not find video to apply the effect";
            break;
        }

        this.notification.error("Effect failed", { body });
        this.cd.detectChanges();
      });
    // this.subscriptions.add(
    //   // only for local video
    //   this.conferenceService.streamMuteWillChange$
    //     .pipe(filter((s) => s.stream.id === this.stream.id))
    //     .subscribe((data) => {
    //       this.muteChange(data.mute, data.trackKind);
    //     })
    // );
    // this.subscriptions.add(
    //   // for remote
    //   this.conferenceService.streamMutedChange$
    //     .pipe(filter((s) => s.stream.id === this.stream.id && !this.isLocal))
    //     .subscribe((data) => {
    //       this.muteChange(data.muted, data.trackKind);
    //     })
    // );
    this.pointerService.freezeFrameChange$
      .pipe(
        filter(
          (e) =>
            e.target.endpoint === this._stream.originator.endpoint &&
            e.correlationId === this._stream.correlationId
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => {
        // this.isFreezeFrameBlurred = false;
        // this.isFreezeFrameVisible = e.on;
        this.isFreezeFrameNotFromMute = e.on;
        this.cd.detectChanges();
      });
    this.pointerService.zoomFrameChange$
      .pipe(
        filter(
          (e) =>
            e.target.endpoint === this._stream.originator.endpoint &&
            e.correlationId === this._stream.correlationId
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => {
        this.isZoomOn = e.on;
        this.cd.detectChanges();
      });

    this.pointerService.pointAreaChange$
      .pipe(
        filter(
          (event) =>
            event.targetUserEndpointId === this.endpoint &&
            event.targetStreamCorrelationId === this._stream.correlationId
          // event.targetStreamType === this._stream.type
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async (event) => {
        switch (event.type) {
          case ArPointerActivatedEvent.type:
            this.isPointerActive.set(true);
            break;
          case ArPointerDeactivatedEvent.type:
            this.isPointerActive.set(false);
            break;
        }
        // wait for animation to end
        setTimeout((_) => this.cd.detectChanges(), 200);
      });
    this.conferenceService.streamMetadataChanged$
      .pipe(filter((e) => e.streamId === this._stream.id), takeUntilDestroyed(this.destroyRef))
      .subscribe((e: IStreamMetadataChangedEvent) => {
        this.ready.emit(this);
      });

    // caused more trouble than what it solved
    this.mediaRules.pictureInPictureChanged$
      .pipe(
        filter((e) => e.streamId === this._stream.id),
        // delay(200)
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => {
        this.isPopedOut = e.on;
        this.cd.detectChanges();
      });

    this.isPointerActive.set(
      this.pointerService.isTarget(
        this.endpoint,
        this.stream.type,
        this.stream.correlationId
      )
    );
  }

  ngAfterViewInit() {
    try {
      // if the tile is too small, the volume indicator oveflows. Listen for resizes and change class accordingly
      this.zone.runOutsideAngular(() => {
        const ro = new ResizeObserver((entries) => {
          const entry = entries?.pop();
          const size = entry?.contentRect.height <= TINY_VIEW_LAYOUT_BREAKPOINT;
          if (size !== this.isTinyView()) {
            this.isTinyView.set(size);
          }

          this.isSketchActionsCondensed =
            entry?.contentRect.width < SKETCH_ACTIONS_COLUMN_LAYOUT_BREAKPOINT;

          this.hostSize.set({
            width: entry?.contentRect.width,
            height: entry?.contentRect.height,
          });
          this.updateViewSize();
          // debug('tile resized notified', this.stream.id, this.hostSize);
          this.cd.markForCheck();
        });
        ro.observe(this.host.nativeElement);
      });
    } catch (ex) {
      debugError(ex);
    }
  }

  @HostListener("document:fullscreenchange")
  @HostListener("document:webkitfullscreenchange")
  fullscreenChanged() {
    const fullscreenElement =
      // eslint-disable-next-line compat/compat
      document.fullscreenElement ||
      document.mozFullScreenElement ||
      document.webkitFullscreenElement;

    this.isFullscreenActive.set(fullscreenElement === this.container);
    this.updateViewSize();
    this.cd.markForCheck();
  }

  /**
   * The tile box is sized to the video ratio by the layout engine, so normally the
   * rendered video fills the whole tile. In fullscreen the layout is bypassed and the
   * video is letterboxed (contain) while annotating, so overlays have to follow the
   * video rect instead of the tile rect.
   */
  protected videoViewSize(): IArea {
    const host = this.hostSize();

    if (!this.isVideoContained()) {
      return host;
    }

    const ratio = this.element?.videoWidth / this.element?.videoHeight;

    if (!ratio || !isFinite(ratio) || !host.width || !host.height) {
      return host;
    }

    return host.width / host.height > ratio
      ? { width: host.height * ratio, height: host.height }
      : { width: host.width, height: host.width / ratio };
  }

  private updateViewSize() {
    const size = this.videoViewSize();

    if (!size.width || !size.height) {
      return;
    }

    this.host.nativeElement.style.setProperty(
      "--av-tile-view-width",
      `${size.width}px`
    );
    this.host.nativeElement.style.setProperty(
      "--av-tile-view-height",
      `${size.height}px`
    );

    this.viewSizeChanged.emit(size);
  }

  ngOnDestroy() {
    this.destroyed.emit(this);
  }

  @HostBinding("class") get class() {
    // debug('tile class generated', this.stream.id, this.isTinyView);
    return {
      "tile-local": this.isLocal,
      "tile-facing-user": this.isLocalMirrored,
      "tile-screen": this.isScreen && !this.isPointerActive(),
      "tile-cam-off": this.isMutedVideo,
      "tile-float": this.isLayoutFloating(),
      "tile-small": this.isTinyView(),
      "tile-portrait": !this.isScreen,
      "tile-pointer-on": this.isPointerActive(),
      "tile-video-contained": this.isVideoContained(),
      "tile-spotlight": this.isSpotlight,
      // 'tile-pip': this.isPopedOut,
      // [`tile-color-${this.color}`]: !!this.color
    };
  }

  private get isLocalMirrored() {
    return (
      ((this.userService.isCustomer &&
        this.config.customerParamEnabled(
          CustomerParam.MIRROR_USER_CAM_ENABLED
        )) ||
        !this.userService.isCustomer) &&
      this.isLocal &&
      this.isFacingModeUser
    );
  }

  // muteChange(mute: boolean, trackKind: StreamTrackKindEnum) {
  //   if (this.isFreezeFrameNotFromMute) {
  //     return;
  //   }

  //   if (mute) {
  //     this.isFreezeFrameVisible =
  //       trackKind === StreamTrackKindEnum.video || this.isMutedVideo;
  //   } else {
  //     setTimeout(() => (this.isFreezeFrameBlurred = false), 200);
  //   }

  //   this.cd.detectChanges();
  // }

  private setAriaLabel() {
    if (this.participant()) {
      const isAgent = this.participant().metadata?.roles?.includes(
        UserRoleEnum.agent
      );

      this.host.nativeElement.setAttribute(
        "aria-label",
        this.translation.instant(
          isAgent || this.isAIAgent() ? "Agent" : "Customer"
        )
      );
    } else {
      this.host.nativeElement.setAttribute(
        "aria-label",
        this.translation.instant("Participant")
      );
    }
  }

  public get isFreezeFrameVisible() {
    return (
      (this.isMutedVideo ||
        this._stream.video.freeze ||
        this.isViewerDisabled) &&
      !(this.isAudioCall && this.isWidget)
    );
  }

  public get isFreezeFrameBlurred() {
    return (
      this.isMutedVideo &&
      !this._stream.video.freeze &&
      !(this.isAudioCall && this.isWidget)
    );
  }

  public get isViewerDisabled() {
    return this._stream.video.disabledFromBitrateAdaptation;
  }

  /* stream element events */

  streamElementReady(element: HTMLVideoElement) {
    this.conferenceService.bindElement(
      (this.element = element),
      this.stream.id
    );

    // TODO: some audio tracks also no canplay, what do
    // for muted streams, 'canplay' event never fires, so we need to inform manually
    if (this.isMutedVideo) {
      this._ratio = 1;
      // debug('tile:streamElementReady');
      this.ready.emit(this);
      this.isStreamElementReady = true;
      this.cd.detectChanges();
    }

    element.addEventListener("canplay", (e) => {
      if (!this._ratio) {
        this._ratio = this.element?.videoWidth / this.element?.videoHeight || 1;
      }
      // debug('tile:canPlay');
      this.ready.emit(this);
      this.isStreamElementReady = true;
      this.updateViewSize();
      this.cd.detectChanges();
    });

    element.addEventListener("resize", (e) => {
      if (
        !this.isStreamElementReady ||
        this.element?.videoWidth === 0 ||
        this.element?.videoHeight === 0
      ) {
        return;
      }

      const ratio = this.element?.videoWidth / this.element?.videoHeight || 1;
      if (ratio !== this._ratio) {
        this._ratio = ratio;
        // debug('tile:resize');
        this.updateViewSize();
        this.ready.emit(this);
      }
    });
  }

  streamElementDestroyed(event: {
    element: HTMLVideoElement;
    stream: MediaStream;
    id: string;
  }) {
    this.conferenceService.unbindElement(event.element, event.id);
  }

  autoplayBlocked(id: string) {
    this.conferenceService.requestExplicitPlay();
  }

  stopScreen() {
    this.mediaService.closeScreenStream();
  }

  avatarError() {
    this.isAvatarError.set(true);
  }

  sketchControlChanged(active: boolean) {
    this.isSketchControlActive = active;
  }

  sketchConnected(id: string) {
    this.sketchId.set(id);
  }

  sketchEnded(id: string) {
    this.sketchId.set(null);
    this.isSketchControlActive = false;
  }

  pointControlSelected() {
    this.isSketchControlActive = false;
  }

  setLayoutType(type: LayoutEnum) {
    this.tileLayoutType.set(type);
  }

  /* view getters */

  isHiddenTooSmall = computed(
    () =>
      (this.hostSize().height < 180 || this.hostSize().width < 180) &&
      this.pointerService.isStarted &&
      !this.pointerService.isTarget(
        this._stream.originator.endpoint,
        this._stream.type,
        this._stream.correlationId
      )
  );

  isSketchAreaActive = computed(
    () => !!this.sketchId() && this.isPointerActive()
  );

  /**
   * the AR pointer's sketch needs a target that can show it (sketch capability); the points work either way.
   * A shared screen is always sketched on, the sketch is drawn over its tile.
   */
  get isSketchSupported(): boolean {
    if (this.isScreen) {
      return true;
    }
    const participant =
      this.conferenceService.getParticipants()?.[
        this._stream?.originator?.endpoint
      ];
    return !participant || SketchService.supported(participant);
  }

  get isPortraitMode() {
    return this._stream.video.portraitMode;
  }

  get isOrientationPortrait() {
    // video is forced to be be in portrait by settings
    return (
      this.isPortraitMode ||
      // video is originally in portrait (mobile phone)
      this.element?.videoWidth < this.element?.videoHeight
    );
  }

  avatar = computed(() => {
    if (this.isAvatarError()) {
      return;
    }
    const isStreamAgent =
      this.stream.originator.username ===
        this.conferenceService.myself.username && this.userService.isAgent;
    if (
      (this.isParticipantAgent() || isStreamAgent) &&
      !!this.config.publicParam(PublicParam.AGENT_AVATAR_MASK)
    ) {
      return this.config.publicParam(PublicParam.AGENT_AVATAR_MASK);
    }
    if (
      this.conferenceService.myself.endpoint ===
      this._stream?.originator?.endpoint
    ) {
      return this.userService.getUserDetails()?.avatarUrl;
    }
    return this.participant()?.metadata?.avatarUrl;
  });

  displayName = computed(() => {
    if (this.isAIAgent() && !!this.theme()?.[ThemeParam.AI_AGENT_NAME]) {
      return this.theme()[ThemeParam.AI_AGENT_NAME];
    }

    if (
      this.isParticipantAgent() &&
      !!(
        this.config.publicParam(PublicParam.AGENT_NAME_MASK) as string
      )?.trim() &&
      this.interaction.getType() !== ConversationTypeEnum.voiceCall
    ) {
      return this.config.publicParam(PublicParam.AGENT_NAME_MASK).trim();
    }
    const name = this.participant()?.metadata?.name?.trim();

    return this.isTinyView() &&
      (this.isRoomLayoutSpotlight() || this.isFloatingInPip()) &&
      !!this.shortName()
      ? this.shortName()
      : name;
  });

  isDisplayNameAvailable = computed(
    () =>
      !!this.displayName() &&
      (!this.isParticipantAgent() ||
        (this.isParticipantAgent() &&
          this.config.publicParam(PublicParam.AGENT_NAME_ENABLED)))
  );

  get uuid() {
    return this._stream.id;
  }

  get type() {
    return this.isScreen
      ? TileTypeEnum.screenShare
      : this.ArPointerOn
      ? TileTypeEnum.arPointer
      : TileTypeEnum.video;
  }

  get isStreamPublished() {
    return "replace" in this._stream;
  }

  get isAudioCall() {
    return this.interaction?.getType() === ConversationTypeEnum.voiceCall;
  }

  get isWidget() {
    return (
      DeviceService.isIFrame &&
      !this.userService.isAgent &&
      this.interaction?.getOrigin() === ConversationOriginEnum.WIDGET
    );
  }

  get ratio() {
    return this.isPortraitMode
      ? 9 / 14
      : this.element &&
        this.element.videoHeight > 0 &&
        this.element.videoWidth > 0
      ? this.element.videoWidth / this.element.videoHeight
      : this._ratio;
  }

  get isScreen() {
    return this._stream.type === StreamTypes.SCREEN;
  }

  get isVideo() {
    return (
      this._stream.type === StreamTypes.VIDEO ||
      this._stream.type === StreamTypes.CAM
    );
  }

  get container() {
    return this.host.nativeElement;
  }

  isLayoutFloating = computed(
    () => this.tileLayoutType() === LayoutEnum.floating
  );

  isRoomLayoutSpotlight = computed(
    () => this.roomLayoutType() === LayoutEnum.spotlight
  );

  /** the self-view floating over the remote participants in the picture-in-picture window */
  isFloatingInPip = computed(
    () =>
      !!this.pipStore?.isOpen() &&
      this.tileLayoutType() === LayoutEnum.floating
  );

  get isFacingModeUser() {
    return this._stream.video.facingMode === VideoFacingModeEnum.User;
  }

  get isDeviceSetup() {
    return this.deviceService.isDevicesSetup;
  }

  get isLocal() {
    return (
      this._stream.originator.endpoint === this.store.endpoint &&
      this._stream.primary
    );
  }

  get isMirror() {
    return this.isLocal && this.isFacingModeUser;
  }

  get isMutedAudio() {
    return (
      this._stream.audio.state === "muted" ||
      this._stream.audio.state === "disabled"
    );
  }

  get isMutedVideo() {
    return (
      this._stream.video.state === "muting" ||
      this._stream.video.state === "muted" ||
      this._stream.video.state === "disabled"
    );
  }

  get endpoint() {
    return this._stream.originator.endpoint;
  }

  isParticipantAgent = computed(() =>
    this.participant()?.metadata?.roles?.includes(UserRoleEnum.agent)
  );

  get isSketchActive() {
    return this.pointerService.isTarget(
      this.endpoint,
      this._stream.type,
      this._stream.correlationId
    );
  }

  get isMirroringHelpActive() {
    return (
      this.isScreen &&
      !DeviceService.isWindowSurface(this.streamDisplaySurface) &&
      // there is nothing else the user could share, the advice is unactionable
      this.mediaService.screenShareSurface !== ScreenShareSurfaceEnum.monitor
    );
  }

  isScreenHelpActive = computed(
    () => this.isScreen && this.isLocal && !this.isPointerActive()
  );

  get streamDisplaySurface() {
    return this.stream.video.display;
  }
}
